import {
  currentThemePref,
  applyThemePref,
  THEME_CHANGE_EVENT,
  type ThemePref,
} from '../theme.js';
import { clearAllNavArrangements } from '../nav-preferences.js';
import { setPreference, PREFERENCES_CHANGE_EVENT } from '../person-preferences.js';
import {
  clearEdgeTabVisibility,
  edgeTabShown,
  onEdgeTabVisibilityChange,
  setEdgeTabShown,
} from '../edge-tab-preferences.js';
import { COMPACT_RAIL_KEY, compactRailOn, setCompactRail } from '../rail-preferences.js';
import { SESSION_SIGN_OUT_ENDPOINT, signOutSession } from '../session-sync.js';
import './profile.css';

/**
 * CAOS-137 — the "My Profile" surface: the signed-in user's record, their appearance preference, the
 * active session, and a personalization reset. It renders in the shell content region (routed at
 * `/profile`, reached from the topbar account menu's "View my profile").
 *
 * Everything here is REAL and functional — no placeholder rows, no dead controls:
 *   • Identity — the real signed-in name + identifier (the session hint), and the confirmed principal
 *     from `/api/auth/whoami` (org + assurance), so the card shows the true session, not a mock.
 *   • Appearance — a System/Light/Dark control bound to the SAME theme seam as the header switch
 *     (theme.ts); changing it re-skins the whole workspace live and persists, and it stays in sync
 *     with the header via the shared theme-change event.
 *   • Session — the active session on this device, with a working Sign out (the real logout path).
 *   • Edge tabs — CAOS-751: one switch per installed edge-anchored source, so a person who never
 *     uses one can stop seeing it. The gutter is on every route by design, so there is nowhere to
 *     navigate away from it; this is the only place that answer can live.
 *   • Personalization — a reset that returns appearance to the system default and clears saved
 *     view preferences, confirmed in place.
 */

const WHOAMI_PATH = '/api/auth/whoami';

/** Injectable fetch seam (production = same-origin `fetch`; tests supply a stub). */
export type FetchImpl = typeof fetch;

/** The confirmed principal from whoami (fail-soft → null). */
interface WhoAmI {
  principalId?: string;
  orgId?: string;
  credential?: string;
  assurance?: string;
}

/** Initials from a display name / email ("Riley Chen" → "RC", "riley@x.io" → "RI"). */
function initials(name: string): string {
  const base = name.includes('@') ? name.split('@')[0]! : name;
  const parts = base.replace(/[._-]+/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** A labelled key/value fact cell. */
function fact(label: string, value: string): HTMLElement {
  const wrap = el('div', 'caos-profile__fact');
  wrap.append(el('span', 'caos-profile__fact-label', label), el('span', 'caos-profile__fact-value', value));
  return wrap;
}

/** A titled section shell. */
function section(title: string, note?: string): { root: HTMLElement; body: HTMLElement } {
  const root = el('section', 'caos-profile__section');
  const head = el('div', 'caos-profile__section-head');
  head.append(el('h2', 'caos-profile__section-title', title));
  if (note) head.append(el('p', 'caos-profile__section-note', note));
  root.append(head);
  return { root, body: root };
}

const THEME_OPTIONS: readonly { pref: ThemePref; label: string }[] = [
  { pref: 'system', label: 'System' },
  { pref: 'light', label: 'Light' },
  { pref: 'dark', label: 'Dark' },
];

/**
 * Render the Profile surface. `userName` is the signed-in display name/identifier (from the session
 * hint); the confirmed principal is read live from whoami. Fail-soft — a whoami hiccup still renders
 * the identity from the session hint.
 */
/**
 * CAOS-372 — the cards this surface can draw. The My Profile app splits them across two sections of
 * its console (who you are, and how you like it), while the standalone `/profile` route still draws
 * all four. ONE builder either way: two places drawing the same card differently is how the same
 * setting ends up meaning two things.
 */
export type ProfileCard = 'account' | 'appearance' | 'edge-tabs' | 'session' | 'personalization';
const ALL_CARDS: readonly ProfileCard[] = ['account', 'appearance', 'edge-tabs', 'session', 'personalization'];

/**
 * CAOS-751 — one edge-anchored source this person can show or hide, as the caller resolved it from
 * the seam the shell already renders. The surface is handed the list rather than reading the shell
 * plan itself: this module draws cards and knows nothing about capability injections, and a profile
 * card that reached into the frame's data would be the second place that decides what is anchored.
 */
export interface EdgeTabChoice {
  readonly sourceKey: string;
  readonly label: string;
}

export interface ProfileSurfaceInput {
  readonly userName?: string;
  /** Which cards to draw, in this order. Absent ⇒ all of them. */
  readonly cards?: readonly ProfileCard[];
  /** Draw the page heading. Absent ⇒ yes; a console section carries its own heading already. */
  readonly heading?: boolean;
  /**
   * CAOS-751 — the edge-anchored sources installed for this org, for the `edge-tabs` card. An empty
   * list is a real answer (nothing anchors here) and says so; ABSENT means the caller does not draw
   * that card at all.
   */
  readonly edgeTabs?: readonly EdgeTabChoice[];
}

export function renderProfileSurface(
  input: ProfileSurfaceInput = {},
  fetchImpl: FetchImpl = fetch,
): HTMLElement {
  const displayName = input.userName?.trim() || 'Signed-in user';
  const cards = new Set(input.cards ?? ALL_CARDS);

  const wrap = el('div', 'caos-frame__page caos-profile');

  // Header.
  if (input.heading !== false) {
    const header = el('header', 'caos-frame__page-header');
    header.append(
      el('div', 'caos-frame__page-eyebrow', 'Profile'),
      el('h1', 'caos-frame__page-title', 'My Profile'),
    );
    wrap.append(header);
  }

  // ── Identity ───────────────────────────────────────────────────────────────
  const idSec = section('Account');
  const identity = el('div', 'caos-profile__identity');
  const avatar = el('div', 'caos-profile__avatar', initials(displayName));
  avatar.setAttribute('aria-hidden', 'true');
  const idText = el('div');
  idText.append(
    el('div', 'caos-profile__id-name', displayName),
    el('div', 'caos-profile__id-sub', input.userName?.includes('@') ? input.userName : 'Signed in'),
  );
  identity.append(avatar, idText);
  idSec.body.append(identity);

  const facts = el('div', 'caos-profile__facts');
  const orgFact = fact('Organization', '—');
  const assuranceFact = fact('Sign-in method', '—');
  facts.append(fact('Name', displayName), orgFact, assuranceFact);
  idSec.body.append(facts);
  if (cards.has('account')) wrap.append(idSec.root);

  // Confirm the real session (org + assurance) from whoami, fail-soft. Only when the card that
  // shows it is being drawn — a read nobody can see is a request spent on nothing.
  if (cards.has('account')) void (async () => {
    try {
      const res = await fetchImpl(WHOAMI_PATH, {
        method: 'GET',
        headers: { accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      const env = (await res.json()) as { ok?: boolean; data?: WhoAmI };
      if (env?.ok === true && env.data) {
        const w = env.data;
        if (w.orgId) orgFact.querySelector('.caos-profile__fact-value')!.textContent = w.orgId;
        if (w.credential || w.assurance) {
          assuranceFact.querySelector('.caos-profile__fact-value')!.textContent = [w.credential, w.assurance]
            .filter(Boolean)
            .join(' · ');
        }
      }
    } catch {
      /* leave the em-dashes — the identity from the session hint still shows. */
    }
  })();

  // ── Appearance (theme) ───────────────────────────────────────────────────────
  const themeSec = section(
    'Appearance',
    'Choose how the workspace looks. System follows your device’s light or dark setting.',
  );
  const segmented = el('div', 'caos-profile__segmented');
  segmented.setAttribute('role', 'group');
  segmented.setAttribute('aria-label', 'Theme');
  const segButtons = new Map<ThemePref, HTMLButtonElement>();
  const reflect = (pref: ThemePref): void => {
    for (const [p, b] of segButtons) b.setAttribute('aria-pressed', p === pref ? 'true' : 'false');
  };
  for (const opt of THEME_OPTIONS) {
    const b = el('button', 'caos-profile__seg', opt.label);
    b.type = 'button';
    b.addEventListener('click', () => applyThemePref(opt.pref));
    segButtons.set(opt.pref, b);
    segmented.append(b);
  }
  reflect(currentThemePref());
  // Keep in sync with the header switch (both drive the same seam via THEME_CHANGE_EVENT).
  const onThemeChange = (ev: Event): void => reflect((ev as CustomEvent<ThemePref>).detail ?? currentThemePref());
  document.addEventListener(THEME_CHANGE_EVENT, onThemeChange);
  // Detach the listener when the surface leaves the DOM, so navigating away doesn't leak it.
  const observer = new MutationObserver(() => {
    if (!wrap.isConnected) {
      document.removeEventListener(THEME_CHANGE_EVENT, onThemeChange);
      // CAOS-752 — the compact-side-tabs control listens to the preference store the same way, and
      // lets go at the same moment, so navigating away leaves neither of them behind.
      document.removeEventListener(PREFERENCES_CHANGE_EVENT, onPreferencesChange);
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  themeSec.body.append(segmented);

  // ── Compact side tabs (CAOS-752) ─────────────────────────────────────────────
  // In Appearance because it is a choice about how the workspace LOOKS, beside the one that decides
  // its colours, rather than a new card for one switch. Two states shown at once, like the theme
  // control: a lone button whose label changes as you press it makes the person read the control to
  // find out what it currently is.
  const compactRow = el('div', 'caos-profile__pref-row');
  compactRow.dataset['preferenceKey'] = COMPACT_RAIL_KEY;
  const compactText = el('div', 'caos-profile__pref-text');
  compactText.append(
    el('div', 'caos-profile__pref-label', 'Compact side tabs'),
    el('p', 'caos-profile__pref-blurb', 'Show the tabs down the right-hand edge as marks only, without their words.'),
  );
  const compact = el('div', 'caos-profile__segmented');
  compact.setAttribute('role', 'group');
  compact.setAttribute('aria-label', 'Compact side tabs');
  const compactButtons: { on: boolean; button: HTMLButtonElement }[] = [];
  const reflectCompact = (): void => {
    for (const b of compactButtons) b.button.setAttribute('aria-pressed', String(b.on === compactRailOn()));
  };
  for (const opt of [
    { on: false, label: 'Off' },
    { on: true, label: 'On' },
  ]) {
    const b = el('button', 'caos-profile__seg', opt.label);
    b.type = 'button';
    // Nothing is reflected here on purpose. The store announces the key it settles on — immediately,
    // and again if the kernel refuses the write — and the listener below is what moves the control.
    // A control that moved itself on the press would keep showing a choice a refusal undid (CAOS-749).
    b.addEventListener('click', () => setCompactRail(opt.on));
    compactButtons.push({ on: opt.on, button: b });
    compact.append(b);
  }
  reflectCompact();
  compactRow.append(compactText, compact);
  themeSec.body.append(compactRow);
  const onPreferencesChange = (ev: Event): void => {
    const keys = (ev as CustomEvent<{ keys?: readonly string[] }>).detail?.keys;
    if (!keys || keys.includes(COMPACT_RAIL_KEY)) reflectCompact();
  };
  document.addEventListener(PREFERENCES_CHANGE_EVENT, onPreferencesChange);

  if (cards.has('appearance')) wrap.append(themeSec.root);

  // ── Edge tabs (CAOS-751) ─────────────────────────────────────────────────────
  // One switch per installed edge-anchored source. The gutter is the one part of the workspace a
  // person cannot navigate away from — it is deliberately present on every route — so "I never use
  // this one" has to be answerable somewhere, and this is that place.
  // Absent `edgeTabs` ⇒ the caller could not resolve what is installed, so the card is not drawn at
  // all. An EMPTY list is a different answer — it resolved, and nothing anchors here — and says so.
  if (cards.has('edge-tabs') && input.edgeTabs) {
    const tabsSec = section(
      'Edge tabs',
      'Choose which tabs appear in the right-hand edge of the workspace. Hiding one only changes your own view.',
    );
    const list = el('div', 'caos-profile__toggles');
    const choices = input.edgeTabs;
    if (choices.length === 0) {
      // A real answer rather than an empty box: nothing is installed that anchors here, which is
      // different from the setting being broken or still loading.
      list.append(el('p', 'caos-profile__empty', 'No packages add a tab to the edge of this workspace.'));
    }
    const boxes = new Map<string, HTMLElement & { checked?: boolean }>();
    for (const choice of choices) {
      const row = el('div', 'caos-profile__toggle');
      const box = document.createElement('caos-checkbox-field') as HTMLElement & { checked?: boolean };
      box.textContent = choice.label;
      box.checked = edgeTabShown(choice.sourceKey);
      box.addEventListener('change', () => {
        void setEdgeTabShown(choice.sourceKey, box.checked === true);
      });
      boxes.set(choice.sourceKey, box);
      row.append(box);
      list.append(row);
    }
    // A choice made on another device — or the reset below — arrives through the same event the
    // gutter itself listens on, so the switches never disagree with the tabs they describe.
    const offVisibility = onEdgeTabVisibilityChange((sourceKey) => {
      const box = boxes.get(sourceKey);
      if (box) box.checked = edgeTabShown(sourceKey);
    });
    // Detach when the card leaves the DOM. `everConnected` is the difference between "removed" and
    // "not mounted YET": this observer watches the whole body, so any unrelated mutation elsewhere
    // can fire it in the window between building this card and inserting it, and tearing down there
    // would silently leave the switches unable to follow a change made anywhere else — with nothing
    // to show for it. The theme listener above this one has the same shape and predates it; it is
    // left as it is rather than changed under this ticket.
    let everConnected = false;
    const tabsObserver = new MutationObserver(() => {
      if (wrap.isConnected) {
        everConnected = true;
        return;
      }
      if (!everConnected) return;
      document.removeEventListener(PREFERENCES_CHANGE_EVENT, offVisibility);
      tabsObserver.disconnect();
    });
    tabsObserver.observe(document.body, { childList: true, subtree: true });
    tabsSec.body.append(list);
    wrap.append(tabsSec.root);
  }

  // ── Active session ───────────────────────────────────────────────────────────
  const sessionSec = section('Session', 'You’re signed in on this device.');
  const sessionFacts = el('div', 'caos-profile__facts');
  sessionFacts.append(fact('Device', 'This browser'), fact('Status', 'Active'));
  sessionSec.body.append(sessionFacts);
  const sessionActions = el('div', 'caos-profile__actions');
  const signOutBtn = el('button', 'caos-profile__btn', 'Sign out');
  signOutBtn.type = 'button';
  signOutBtn.addEventListener('click', () => {
    signOutBtn.setAttribute('aria-disabled', 'true');
    void (async () => {
      try {
        await signOutSession(() =>
          fetchImpl(SESSION_SIGN_OUT_ENDPOINT, { method: 'POST', credentials: 'same-origin' }),
        );
      } finally {
        window.location.assign('/');
      }
    })();
  });
  sessionActions.append(signOutBtn);
  sessionSec.body.append(sessionActions);
  if (cards.has('session')) wrap.append(sessionSec.root);

  // ── Personalization reset ────────────────────────────────────────────────────
  const resetSec = section(
    'Personalization',
    'Return appearance, your customised app navigation, your edge tabs, and saved view preferences to their defaults, wherever you sign in. Only your own view changes.',
  );
  const resetActions = el('div', 'caos-profile__actions');
  const resetBtn = el('button', 'caos-profile__btn caos-profile__btn--danger', 'Reset personalization');
  resetBtn.type = 'button';
  const confirm = el('span', 'caos-profile__confirm');
  resetBtn.addEventListener('click', () => {
    // CAOS-470 — clear the person's own theme so the org default (or the system setting) applies again,
    // rather than recording "system" as a new choice. Fires THEME_CHANGE_EVENT → controls reflect.
    void setPreference('theme', null);
    applyThemePref(currentThemePref());
    // CAOS-580 — every app goes back to its published navigation. This is the one control that
    // undoes a Customize Navigation arrangement wholesale, so it has to actually clear it: a reset
    // that leaves the tab rows rearranged is the same broken promise from the other direction.
    clearAllNavArrangements();
    // CAOS-751 — and the gutter. A reset that left tabs hidden would leave the one surface a person
    // cannot navigate away from still altered, while telling them everything was back to defaults.
    clearEdgeTabVisibility();
    // CAOS-752 — and the side tabs go back to carrying their words. This card promises to return
    // APPEARANCE to its defaults, and a reset that left the rail compact would be that promise half
    // kept. Cleared rather than set to `false`, so an org default still applies, exactly like theme.
    void setPreference(COMPACT_RAIL_KEY, null);
    try {
      // Clear any saved per-user view preferences (reserved keys), best-effort.
      for (let i = window.localStorage.length - 1; i >= 0; i--) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith('caos-view-')) window.localStorage.removeItem(key);
      }
    } catch {
      /* storage unavailable — the appearance reset still applied. */
    }
    confirm.textContent = 'Personalization reset to defaults.';
  });
  resetActions.append(resetBtn, confirm);
  resetSec.body.append(resetActions);
  if (cards.has('personalization')) wrap.append(resetSec.root);

  return wrap;
}
